import { useCallback, useEffect, useState } from "react";
import { Cpu, Cloud, FlaskConical, Sparkles, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EngineKeyForm } from "@/components/engines/EngineKeyForm";
import { RunPodWorkerPanel } from "@/components/engines/RunPodWorkerPanel";

const ICONS = { external_api: Cloud, local_model: Cpu, mock: FlaskConical, future: Sparkles };

export default function Engines() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [sys, setSys] = useState(null);
  const load = useCallback(() => api.get("/engines").then((r) => setData(r.data)), []);
  useEffect(() => {
    load();
    api.get("/system").then((r) => setSys(r.data));
  }, [load]);
  if (!data) return null;
  return (
    <div data-testid="engine-settings-view" className="space-y-4 fade-up">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Separation Engines</h1>
        <p className="text-sm text-slate-400 mt-1">Every engine implements the same <code className="text-cyan-300">SeparationEngine</code> interface (process · get_status · get_results · cancel). Keys live server-side (Engines page → Mongo settings, or <code>backend/.env</code>). Paid engines are marked and are never pre-selected; no engine is treated as preferred until picture-based QC in FCP.</p>
      </div>
      <RunPodWorkerPanel isAdmin={user?.role === "admin"} onChanged={load} />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.engines.map((e) => {
          const Icon = ICONS[e.kind] || Cloud;
          return (
            <div key={e.key} data-testid={`engine-card-${e.key}`} className="card-surface p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Icon size={16} className="text-slate-400" />
                  <div>
                    <div className="font-display font-semibold">{e.name}</div>
                    <div className="text-[11px] text-slate-500">{e.vendor} · {e.kind.replace("_", " ")}{e.key === data.default ? " · default" : ""}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                {e.paid && <span data-testid={`engine-paid-${e.key}`} className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border text-amber-300 border-amber-700 bg-amber-400/10">paid</span>}
                <span data-testid={`engine-status-${e.key}`} className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${e.configured ? "text-emerald-400 border-emerald-700 bg-emerald-400/10" : "text-amber-400 border-amber-700 bg-amber-400/10"}`}>
                  {e.configured ? "ready" : "not configured"}
                </span>
                </div>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">{e.description}</p>
              <div className="text-[11px] text-slate-500 italic">{e.quality_note}</div>
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono mt-auto">
                <div><div className="micro">Model</div><div className="text-slate-200 truncate" title={e.model_name}>{e.model_name}</div></div>
                <div><div className="micro">Version</div><div className="text-slate-200 truncate" title={e.model_version}>{e.model_version}</div></div>
                <div><div className="micro">Cost / min</div><div className="text-slate-200">{e.cost_per_minute == null ? "unknown" : `${e.cost_per_minute} ${e.cost_unit || ""}`}</div></div>
                <div><div className="micro">Stems</div><div className="text-slate-200 uppercase">{e.supported_stems.join(" ")}</div></div>
              </div>
              <div className="text-[11px] font-mono text-slate-400 border-t border-[#253044] pt-2 break-all">{e.detail}</div>
              {e.editable && <EngineKeyForm engine={e} isAdmin={user?.role === "admin"} onSaved={load} />}
              {e.key === "opensource" && (
                <div className="border-t border-[#253044] pt-2 text-[11px] text-slate-400 leading-relaxed">
                  GPU worker package: <code className="text-cyan-300">/app/workers/bandit_worker/</code> (README + run_bandit.py). Point <code>OPEN_SOURCE_CMD</code> at it once the BandIt-v2 checkpoint is installed on a CUDA machine.
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="panel p-4 grid md:grid-cols-3 gap-4 text-xs">
        <div><div className="micro flex items-center gap-1"><ShieldCheck size={11} /> Storage</div><div className="font-mono text-slate-200 mt-1">{sys?.storage?.backend}</div><div className="text-slate-500 mt-0.5">Sources, working masters and stems kept under separate prefixes · signed private URLs · {sys?.storage?.free_gb} GB local cache free</div></div>
        <div><div className="micro">Output policy</div><div className="text-slate-300 mt-1">WAV · 24-bit PCM · source sample rate preserved (48 kHz for lossy inputs) · channel count preserved · never MP3</div></div>
        <div><div className="micro">Presets</div><div className="font-mono text-slate-300 mt-1">{Object.entries(data.presets).map(([k, v]) => `${k}: ${v.join("+")}`).join(" · ")}</div></div>
      </div>
    </div>
  );
}
